import { useEffect, useState } from 'react';

// Just under 1 so sub-pixel rounding on a fully-visible target doesn't hide it.
const MIN_VISIBLE_RATIO = 0.95;

// Occlusion (getComputedStyle + elementFromPoint) is far pricier than the clip
// read and only changes when a modal opens/closes, so it's sampled on this
// interval rather than every frame. Well under any modal's open/close animation.
const OCCLUSION_INTERVAL_MS = 150;

const CLIPPING_OVERFLOW = /(auto|scroll|hidden|clip)/;

// Ancestors that clip overflow (e.g. a scrollable modal body).
function getClippingAncestors(element: HTMLElement): HTMLElement[] {
  const ancestors: HTMLElement[] = [];
  let node = element.parentElement;
  while (node) {
    const style = window.getComputedStyle(node);
    if (
      CLIPPING_OVERFLOW.test(style.overflowX) ||
      CLIPPING_OVERFLOW.test(style.overflowY)
    ) {
      ancestors.push(node);
    }
    node = node.parentElement;
  }
  return ancestors;
}

/**
 * Fraction of the target still on-screen after clipping its rect against the
 * viewport and every overflow-clipping ancestor. Computed from live rects each
 * frame, so it reflects the target's resting position the moment a scroll ends —
 * no async settle lag, unlike an IntersectionObserver's crossing callbacks.
 */
function getVisibleRatio(
  targetRect: DOMRect,
  ancestorRects: DOMRect[]
): number {
  const area = targetRect.width * targetRect.height;
  if (!area) return 0;

  let { top, left, right, bottom } = targetRect;
  const clips = [
    { top: 0, left: 0, right: window.innerWidth, bottom: window.innerHeight },
    ...ancestorRects,
  ];
  for (const clip of clips) {
    top = Math.max(top, clip.top);
    left = Math.max(left, clip.left);
    right = Math.min(right, clip.right);
    bottom = Math.min(bottom, clip.bottom);
  }

  const visibleArea = Math.max(0, right - left) * Math.max(0, bottom - top);
  return visibleArea / area;
}

/**
 * Whether `element` is buried under an *external* overlay (e.g. an open modal) —
 * something rect geometry alone can't see. Scroll-out and in-container chrome
 * are deliberately not counted; those are clipping (a fade), not occlusion.
 */
function isElementOccluded(
  element: HTMLElement,
  targetRect: DOMRect,
  clippingAncestors: HTMLElement[],
  ancestorRects: DOMRect[]
): boolean {
  // Can't hit-test a pointer-events:none target (also skips the highlight overlay).
  if (window.getComputedStyle(element).pointerEvents === 'none') return false;

  if (!targetRect.width || !targetRect.height) return false;

  const x = targetRect.left + targetRect.width / 2;
  const y = targetRect.top + targetRect.height / 2;

  // Center scrolled outside a clipping ancestor → clipped there, not occluded.
  for (const bounds of ancestorRects) {
    if (
      x < bounds.left ||
      x > bounds.right ||
      y < bounds.top ||
      y > bounds.bottom
    ) {
      return false;
    }
  }

  const topElement = document.elementFromPoint(x, y);
  if (!topElement) return false;
  // Our own tooltip sitting near the target isn't an occluder.
  if (topElement.closest('[data-onboarding]')) return false;
  // Target's own subtree/ancestor showing through → visible.
  if (element.contains(topElement) || topElement.contains(element)) {
    return false;
  }
  // Chrome inside the target's own scroll container (e.g. a sticky header) is
  // not an external overlay.
  const nearestClip = clippingAncestors[0];
  if (nearestClip && nearestClip.contains(topElement)) return false;

  return true;
}

export type TargetVisibility = {
  /** On-screen: unclipped enough and not occluded. Drives the fade. */
  isVisible: boolean;
  /** Buried under an external overlay — distinct from scrolled-off so callers
   * can dequeue only in that case. */
  isOccluded: boolean;
};

/**
 * Tracks whether `targetElement` is clipped out of its boundary and/or occluded,
 * so the onboarding highlight and tooltip appear and disappear together.
 */
export function useTargetVisibility(
  targetElement: HTMLElement | null
): TargetVisibility {
  // Assume visible until proven otherwise, so first paint isn't withheld a tick.
  const [visibility, setVisibility] = useState<TargetVisibility>({
    isVisible: true,
    isOccluded: false,
  });

  useEffect(() => {
    if (!targetElement) return undefined;

    // A target bigger than its clip container can never reach the ratio and would
    // hide forever, so only enforce the cutoff once it's been fully visible once.
    let hasBeenFullyVisible = false;
    let rafId: number | null = null;
    // Carried between frames so occlusion can be sampled less often than the clip.
    let isOccluded = false;
    let lastOcclusionCheck = 0;

    const clippingAncestors = getClippingAncestors(targetElement);

    const check = (now: number) => {
      const targetRect = targetElement.getBoundingClientRect();
      const ancestorRects = clippingAncestors.map((ancestor) =>
        ancestor.getBoundingClientRect()
      );

      const ratio = getVisibleRatio(targetRect, ancestorRects);
      if (ratio >= MIN_VISIBLE_RATIO) hasBeenFullyVisible = true;
      const isUnclipped = ratio >= MIN_VISIBLE_RATIO || !hasBeenFullyVisible;

      if (now - lastOcclusionCheck >= OCCLUSION_INTERVAL_MS) {
        lastOcclusionCheck = now;
        isOccluded = isElementOccluded(
          targetElement,
          targetRect,
          clippingAncestors,
          ancestorRects
        );
      }

      const isVisible = isUnclipped && !isOccluded;
      setVisibility((prev) =>
        prev.isVisible === isVisible && prev.isOccluded === isOccluded
          ? prev
          : { isVisible, isOccluded }
      );
      rafId = window.requestAnimationFrame(check);
    };
    rafId = window.requestAnimationFrame(check);

    return () => {
      if (rafId != null) window.cancelAnimationFrame(rafId);
    };
  }, [targetElement]);

  return visibility;
}
